import { useState, useEffect, useRef, type MouseEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { Checkbox } from '../../components/ui/controls'
import { Notice } from '../../components/legal/LegalPresentation'
import { toast as message } from 'sonner'
import { useAuthStore } from '../../stores/authStore'
import LegalTermsModal from '../../components/legal/LegalTermsModal'
import {
  login,
  register,
  sendCode,
  verifyLoginEmail,
  verifyLoginTwoFactor,
} from '../../services/auth'
import { getLegalConfig } from '../../services/config'
import { LEGAL_ROUTES, LEGAL_TERMS_VERSION } from '../../constants/legal'
import { LegalDocKey } from '../../constants/legalContent'
import TurnstileWidget, {
  type TurnstileWidgetHandle,
} from '../../components/common/TurnstileWidget'
import EmailCodeVerification from '../../components/common/EmailCodeVerification'
import { useEmailCodeVerification } from '../../hooks/useEmailCodeVerification'

interface LoginForm {
  email: string
  password: string
  totpCode?: string
  emailCode?: string
}

interface RegisterForm {
  email: string
  code: string
  password: string
  confirmPassword: string
  inviteCode?: string
  agreeToTerms: boolean
}

export default function Landing() {
  const [searchParams] = useSearchParams()
  const inviteCodeFromUrl =
    searchParams.get('code')?.trim() ||
    searchParams.get('inviteCode')?.trim() ||
    undefined
  const initialMode =
    searchParams.get('mode') === 'register' || inviteCodeFromUrl
      ? 'register'
      : 'login'
  const [activeTab, setActiveTab] = useState<string>(initialMode)
  const [loginLoading, setLoginLoading] = useState(false)
  const [registerLoading, setRegisterLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [legalTermsVersion, setLegalTermsVersion] =
    useState(LEGAL_TERMS_VERSION)
  const [legalModalOpen, setLegalModalOpen] = useState(false)
  const [activeLegalDoc, setActiveLegalDoc] =
    useState<LegalDocKey>('userAgreement')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const [loginVerificationMethod, setLoginVerificationMethod] = useState<
    'email' | 'totp' | null
  >(null)
  const [loginVerificationError, setLoginVerificationError] = useState<
    string | null
  >(null)
  const [loginChallengeToken, setLoginChallengeToken] = useState<string | null>(
    null,
  )
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [loginValues, setLoginValues] = useState<LoginForm>({
    email: '',
    password: '',
    totpCode: '',
    emailCode: '',
  })
  const [registerValues, setRegisterValues] = useState<RegisterForm>({
    email: '',
    code: '',
    password: '',
    confirmPassword: '',
    inviteCode: inviteCodeFromUrl,
    agreeToTerms: false,
  })
  const registerEmail = registerValues.email
  const emailVerification = useEmailCodeVerification(
    `register:${activeTab}:${registerEmail?.trim() ?? ''}`,
  )
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY
  const loginEmail = loginValues.email
  const loginPassword = loginValues.password

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  useEffect(() => {
    let cancelled = false
    getLegalConfig()
      .then((res) => {
        if (!cancelled && res.data?.legalTermsVersion) {
          setLegalTermsVersion(res.data.legalTermsVersion)
        }
      })
      .catch(() => {
        // Keep local fallback when config API is temporarily unavailable.
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (inviteCodeFromUrl) {
      setRegisterValues((prev) => ({ ...prev, inviteCode: inviteCodeFromUrl }))
    }
  }, [inviteCodeFromUrl])

  useEffect(() => {
    if (!loginChallengeToken) {
      return
    }
    setLoginChallengeToken(null)
    setLoginVerificationMethod(null)
    setLoginVerificationError(null)
    setLoginValues((prev) => ({ ...prev, totpCode: '', emailCode: '' }))
    setTurnstileToken(null)
    setTurnstileError(null)
    turnstileRef.current?.reset()
  }, [loginEmail, loginPassword])

  const openLegalModal = (docKey: LegalDocKey) => {
    setActiveLegalDoc(docKey)
    setLegalModalOpen(true)
  }

  const handleLegalLinkClick = (
    event: MouseEvent<HTMLAnchorElement>,
    docKey: LegalDocKey,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    openLegalModal(docKey)
  }

  const handleLogin = async (values: LoginForm) => {
    const challengeToken = loginChallengeToken
    if (!challengeToken && turnstileSiteKey && !turnstileToken) {
      const nextMessage = turnstileError ?? '请先完成人机验证'
      setTurnstileError(nextMessage)
      message.warning(nextMessage)
      return
    }
    if (challengeToken) {
      const verificationCode =
        loginVerificationMethod === 'email' ? values.emailCode : values.totpCode
      if (!/^\d{6}$/.test(verificationCode ?? '')) {
        setLoginVerificationError('請輸入 6 位數字驗證碼')
        return
      }
      setLoginVerificationError(null)
    }
    setLoginLoading(true)
    try {
      if (challengeToken) {
        const res =
          loginVerificationMethod === 'email'
            ? await verifyLoginEmail(challengeToken, values.emailCode ?? '')
            : await verifyLoginTwoFactor(challengeToken, values.totpCode ?? '')
        if (!res.data.token || !res.data.merchant) {
          throw new Error('登录响应缺少认证信息')
        }
        setAuth(res.data.token, res.data.merchant)
        message.success('登入成功')
        navigate('/dashboard')
        return
      }

      const res = await login(
        values.email,
        values.password,
        turnstileToken || undefined,
      )
      if (
        (res.data.requiresTwoFactor || res.data.requiresEmailVerification) &&
        res.data.loginChallengeToken
      ) {
        const verificationMethod = res.data.requiresTwoFactor
          ? 'totp'
          : 'email'
        setLoginVerificationMethod(verificationMethod)
        setLoginChallengeToken(res.data.loginChallengeToken)
        setLoginVerificationError(null)
        setLoginValues((prev) => ({
          ...prev,
          totpCode: '',
          emailCode: '',
        }))
        message.info(
          verificationMethod === 'totp'
            ? '账号已开启 2FA，请输入 TOTP 验证码继续登录'
            : '驗證碼已發送。如果沒有收到，請先檢查垃圾郵箱。',
        )
        return
      }
      if (!res.data.token || !res.data.merchant) {
        throw new Error('登录响应缺少认证信息')
      }
      setAuth(res.data.token, res.data.merchant)
      message.success('登入成功')
      navigate('/dashboard')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '登入失敗')
      if (challengeToken) {
        setLoginChallengeToken(null)
        setLoginVerificationMethod(null)
        setLoginVerificationError(null)
        setLoginValues((prev) => ({ ...prev, totpCode: '', emailCode: '' }))
        setTurnstileToken(null)
        setTurnstileError(null)
        turnstileRef.current?.reset()
      } else {
        turnstileRef.current?.reset()
      }
    } finally {
      setLoginLoading(false)
    }
  }

  const showLoginVerification = Boolean(loginChallengeToken)
  const showLoginEmailField =
    showLoginVerification && loginVerificationMethod === 'email'
  const showLoginTotpField =
    showLoginVerification && loginVerificationMethod === 'totp'
  const shouldRequireTurnstile = Boolean(
    !showLoginVerification && turnstileSiteKey,
  )
  const loginDisabled = Boolean(shouldRequireTurnstile && !turnstileToken)

  const handleSendCode = async () => {
    try {
      const email = registerValues.email
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        message.error('請輸入有效的郵箱地址')
        return
      }
      if (!email) {
        message.error('請先輸入郵箱')
        return
      }
      setSendingCode(true)
      await emailVerification.send((token) =>
        sendCode(email, 'register', token),
      )
      message.success('驗證碼已發送。如果沒有收到，請先檢查垃圾郵箱。')
      setCountdown(60)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '發送驗證碼失敗')
    } finally {
      setSendingCode(false)
    }
  }

  const handleRegister = async (values: RegisterForm) => {
    if (!emailVerification.ready) {
      message.warning('請先完成人機驗證')
      return
    }
    if (values.password !== values.confirmPassword) {
      message.error('兩次輸入的密碼不一致')
      return
    }
    if (!values.agreeToTerms) {
      message.warning('請先同意相關法律條款')
      return
    }
    setRegisterLoading(true)
    try {
      const res = await register(
        values.email,
        values.password,
        values.code,
        values.agreeToTerms,
        legalTermsVersion,
        values.inviteCode?.trim() || undefined,
      )
      if (!res.data.token || !res.data.merchant) {
        throw new Error('注册响应缺少认证信息')
      }
      setAuth(res.data.token, res.data.merchant)
      message.success('註冊成功')
      navigate('/dashboard')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '註冊失敗')
    } finally {
      setRegisterLoading(false)
    }
  }

  const features = [
    { icon: '🚀', title: '低門檻開戶', desc: '快速註冊，即刻開始收款' },
    { icon: '🛡️', title: '合規收款', desc: '標準化收單，安全可靠' },
    { icon: '💸', title: '快速結算', desc: 'KYC 通過後，靈活結款' },
  ]

  const loginPanel = (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        void handleLogin(loginValues)
      }}
    >
      <label className="block text-sm">
        郵箱地址
        <Input
          className="mt-2"
          aria-label="郵箱地址"
          placeholder="郵箱地址"
          type="email"
          required
          value={loginValues.email}
          onChange={(event) =>
            setLoginValues((prev) => ({ ...prev, email: event.target.value }))
          }
        />
      </label>
      <label className="block text-sm">
        密碼
        <Input
          className="mt-2"
          aria-label="密碼"
          placeholder="密碼"
          type="password"
          required
          value={loginValues.password}
          onChange={(event) =>
            setLoginValues((prev) => ({
              ...prev,
              password: event.target.value,
            }))
          }
        />
      </label>
      {showLoginEmailField && (
        <>
          <Notice title="請驗證您的郵箱">
            驗證碼已發送至您的郵箱。如果沒有收到，請先檢查垃圾郵箱。登入驗證 5 分鐘內有效。
          </Notice>
          <Input
            aria-label="郵箱驗證碼"
            placeholder="請輸入 6 位郵箱驗證碼"
            required
            inputMode="numeric"
            maxLength={6}
            value={loginValues.emailCode || ''}
            onChange={(event) => {
              setLoginVerificationError(null)
              setLoginValues((prev) => ({
                ...prev,
                emailCode: event.target.value,
              }))
            }}
          />
        </>
      )}
      {showLoginTotpField ? (
        <>
          <Notice title="账号已开启 2FA">
            请输入身份验证器中的 6 位 TOTP 验证码完成登录。
          </Notice>
          <Input
            aria-label="2FA 驗證碼"
            placeholder="請輸入 6 位驗證碼"
            required
            inputMode="numeric"
            maxLength={6}
            value={loginValues.totpCode || ''}
            onChange={(event) => {
              setLoginVerificationError(null)
              setLoginValues((prev) => ({
                ...prev,
                totpCode: event.target.value,
              }))
            }}
          />
        </>
      ) : !showLoginVerification && turnstileSiteKey ? (
        <TurnstileWidget
          ref={turnstileRef}
          siteKey={turnstileSiteKey}
          onTokenChange={setTurnstileToken}
          onErrorChange={setTurnstileError}
        />
      ) : null}
      {loginVerificationError && (
        <Notice tone="error" title="驗證碼格式不正確">
          {loginVerificationError}
        </Notice>
      )}
      {!showLoginVerification && turnstileSiteKey && turnstileError && (
        <Notice tone="error" title="Turnstile 验证不可用">
          {turnstileError}
        </Notice>
      )}
      <Button
        type="submit"
        disabled={loginDisabled || loginLoading}
        className="h-11 w-full"
        data-testid="login-submit"
      >
        {loginLoading
          ? '登入中…'
          : showLoginVerification
            ? showLoginTotpField
              ? '驗證 2FA'
              : '驗證郵箱'
            : '登入'}
      </Button>
      {showLoginVerification && (
        <Button
          type="button"
          variant="ghost"
          disabled={loginLoading}
          className="w-full"
          onClick={() => {
            setLoginChallengeToken(null)
            setLoginVerificationMethod(null)
            setLoginVerificationError(null)
            setLoginValues((prev) => ({
              ...prev,
              totpCode: '',
              emailCode: '',
            }))
            setTurnstileToken(null)
            setTurnstileError(null)
            turnstileRef.current?.reset()
          }}
        >
          返回登入
        </Button>
      )}
      <div className="text-center">
        <a
          href="/reset-password"
          className="text-sm text-primary"
          onClick={(event) => {
            event.preventDefault()
            navigate('/reset-password')
          }}
        >
          忘記密碼？
        </a>
      </div>
    </form>
  )
  const registrationPanel = (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        void handleRegister(registerValues)
      }}
    >
      <label className="block text-sm">
        郵箱地址
        <Input
          className="mt-2"
          aria-label="郵箱地址"
          placeholder="郵箱地址"
          type="email"
          required
          value={registerValues.email}
          onChange={(event) =>
            setRegisterValues((prev) => ({
              ...prev,
              email: event.target.value,
            }))
          }
        />
      </label>
      <label className="block text-sm">
        驗證碼
        <div className="mt-2 flex gap-2">
          <Input
            aria-label="驗證碼"
            placeholder="驗證碼"
            required
            value={registerValues.code}
            onChange={(event) =>
              setRegisterValues((prev) => ({
                ...prev,
                code: event.target.value,
              }))
            }
          />
          <Button
            type="button"
            variant="outline"
            disabled={
              sendingCode || countdown > 0 || !emailVerification.canSend
            }
            onClick={handleSendCode}
            data-testid="send-code-btn"
          >
            {countdown > 0
              ? `${countdown}s`
              : sendingCode
                ? '發送中…'
                : '獲取驗證碼'}
          </Button>
        </div>
      </label>
      <label className="block text-sm">
        密碼
        <Input
          className="mt-2"
          aria-label="密碼"
          placeholder="密碼（至少8位）"
          type="password"
          minLength={8}
          required
          value={registerValues.password}
          onChange={(event) =>
            setRegisterValues((prev) => ({
              ...prev,
              password: event.target.value,
            }))
          }
        />
      </label>
      <label className="block text-sm">
        確認密碼
        <Input
          className="mt-2"
          aria-label="確認密碼"
          placeholder="確認密碼"
          type="password"
          required
          value={registerValues.confirmPassword}
          onChange={(event) =>
            setRegisterValues((prev) => ({
              ...prev,
              confirmPassword: event.target.value,
            }))
          }
        />
      </label>
      <label className="block text-sm">
        邀請碼（可選）
        <Input
          className="mt-2"
          aria-label="邀請碼"
          placeholder="邀請碼（可選）"
          value={registerValues.inviteCode || ''}
          onChange={(event) =>
            setRegisterValues((prev) => ({
              ...prev,
              inviteCode: event.target.value,
            }))
          }
        />
      </label>
      <div className="flex items-start gap-2 text-sm leading-relaxed">
        <Checkbox
          id="registration-consent"
          checked={registerValues.agreeToTerms}
          onCheckedChange={(checked) =>
            setRegisterValues((prev) => ({ ...prev, agreeToTerms: checked }))
          }
        />
        <div>
          <label htmlFor="registration-consent">我已閱讀並同意 </label>
          {(
            [
              'userAgreement',
              'privacyPolicy',
              'disclaimer',
              'acceptableUsePolicy',
            ] as LegalDocKey[]
          ).map((key, index) => (
            <span key={key}>
              {index > 0 ? '、' : ''}
              <a
                href={LEGAL_ROUTES[key]}
                className="text-primary hover:underline"
                onClick={(event) => handleLegalLinkClick(event, key)}
              >
                {
                  [
                    '《用戶協議》',
                    '《隱私政策》',
                    '《免責聲明》',
                    '《可接受使用政策》',
                  ][index]
                }
              </a>
            </span>
          ))}
        </div>
      </div>
      <EmailCodeVerification verification={emailVerification} />
      <Button
        type="submit"
        disabled={registerLoading || !emailVerification.ready}
        className="h-11 w-full"
        data-testid="register-submit"
      >
        {registerLoading ? '註冊中…' : '註冊'}
      </Button>
    </form>
  )

  return (
    <div className="flex min-h-screen flex-col bg-[#faf9fd] text-slate-900">
      <main className="mx-auto grid w-full max-w-6xl flex-1 items-center gap-12 px-5 py-12 lg:grid-cols-2 lg:gap-24">
        <section>
          <div className="mb-10 flex items-center gap-3 text-xl font-semibold">
            <span className="flex size-10 items-center justify-center rounded-xl bg-primary text-white">
              K
            </span>
            KyrenPay
          </div>
          <h1 className="text-3xl font-semibold leading-tight sm:text-4xl">
            面向中國出海 AI Startup
            <br />
            的收款與結算平台
          </h1>
          <p className="mt-5 text-muted-foreground">
            低門檻開戶，合規收款，快速結算
          </p>
          <div className="mt-10 space-y-5">
            {features.map((feature) => (
              <div className="flex items-center gap-4" key={feature.title}>
                <span className="flex size-11 items-center justify-center rounded-xl bg-violet-100 text-xl">
                  {feature.icon}
                </span>
                <div>
                  <h2 className="font-medium">{feature.title}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
        <section className="rounded-2xl border bg-white p-6 shadow-sm sm:p-9">
          <div
            role="tablist"
            aria-label="帳戶"
            className="mb-7 grid grid-cols-2 gap-2 rounded-xl bg-muted p-1"
          >
            {['login', 'register'].map((tab) => (
              <button
                type="button"
                id={`auth-tab-${tab}`}
                role="tab"
                aria-selected={activeTab === tab}
                aria-controls="auth-panel"
                key={tab}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${activeTab === tab ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'}`}
                onClick={() => {
                  emailVerification.reset()
                  setActiveTab(tab)
                }}
              >
                {tab === 'login' ? '登入' : '註冊'}
              </button>
            ))}
          </div>
          <div
            role="tabpanel"
            id="auth-panel"
            aria-labelledby={`auth-tab-${activeTab}`}
          >
            {activeTab === 'login' ? loginPanel : registrationPanel}
          </div>
        </section>
      </main>
      <footer className="flex flex-wrap justify-center gap-x-4 gap-y-2 border-t px-5 py-5 text-xs text-muted-foreground">
        <span>© 2026 Kyren</span>
        <a href="mailto:support@kyrenpay.com">support@kyrenpay.com</a>
        {(
          [
            'userAgreement',
            'privacyPolicy',
            'disclaimer',
            'acceptableUsePolicy',
          ] as LegalDocKey[]
        ).map((key, index) => (
          <a
            key={key}
            href={LEGAL_ROUTES[key]}
            onClick={(event) => handleLegalLinkClick(event, key)}
          >
            {['用戶協議', '隱私政策', '免責聲明', 'AUP'][index]}
          </a>
        ))}
      </footer>

      <LegalTermsModal
        open={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        initialDocKey={activeLegalDoc}
        legalTermsVersion={legalTermsVersion}
      />
    </div>
  )
}
